// src/lib/favorites.ts
//
// Per-account product favourites.
//
// The list lives on the SERVER, keyed by the Keycloak identity
// (issuer, sub) — the same key the account language preference uses
// (src/lib/langPreference.ts). A favourite therefore follows the
// ACCOUNT across devices, reloads and sign-outs, and two people of the
// SAME customer account keep two separate lists.
//
// Cookie-session only, like /api/auth/preferences: the browser holds an
// HttpOnly session cookie and echoes the per-session CSRF token on
// every write. An anonymous visitor has no favourites, and a bearer API
// client never owns a browser shortlist.
//
// The product VISIBILITY rules still apply: a hidden product (or a
// product whose parent brand is hidden) can neither be added nor
// returned by the API.

import { authHeaders, type ApiProduct } from "@/lib/api";

/** One favourite row as the API returns it (public product shape). */
export type FavoriteProduct = ApiProduct;

export interface FavoritesSnapshot {
  /** Favourited product ids, most recently added first. */
  ids: number[];
  /** The favourite products themselves (public projection). */
  products: FavoriteProduct[];
  /** True while the list is being (re)loaded. */
  loading: boolean;
  /** The last load/write error, or null. */
  error: string | null;
  /** Product ids with a write in flight (the button shows a spinner). */
  pending: number[];
  /**
   * Product ids inside the post-write cooldown. A click in that window
   * is absorbed, so the button says so — it is disabled, with the wait
   * cursor — rather than looking clickable and quietly ignoring the
   * click.
   */
  locked: number[];
}

const EMPTY: FavoritesSnapshot = {
  ids: [],
  products: [],
  loading: false,
  error: null,
  pending: [],
  locked: [],
};

let snapshot: FavoritesSnapshot = EMPTY;
const listeners = new Set<() => void>();

/**
 * Bumped on every reset. A response that arrives after a reset (an
 * account switch) belongs to the PREVIOUS account and must never be
 * applied to the current one.
 */
let generation = 0;

function setSnapshot(next: Partial<FavoritesSnapshot>) {
  snapshot = { ...snapshot, ...next };
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): FavoritesSnapshot {
  return snapshot;
}

/**
 * Drop every trace of the previous account. Called when the signed-in
 * identity changes: the next account must never see the previous
 * account's favourites, not even for one render.
 */
export function resetFavorites() {
  generation += 1;
  snapshot = EMPTY;
  // Drop the write locks too: a pending timer must never delete a
  // NEW account's in-flight write.
  for (const timer of lockTimers.values()) clearTimeout(timer);
  lockTimers.clear();
  inFlight.clear();
  for (const l of listeners) l();
}

/**
 * Load the signed-in account's favourites. Fails closed: on error the
 * list stays empty and an error message is exposed. A 401 (signed out)
 * is not an error — an anonymous visitor simply has no favourites.
 */
export async function loadFavorites(): Promise<void> {
  const gen = generation;
  setSnapshot({ loading: true, error: null });
  try {
    const r = await fetch("/api/auth/favorites", {
      credentials: "same-origin",
      cache: "no-store",
    });
    // A reset happened while we were waiting: this response belongs to
    // the previous account — drop it.
    if (gen !== generation) return;
    if (!r.ok) {
      setSnapshot({
        loading: false,
        error: r.status === 401 ? null : `HTTP ${r.status}`,
        ...(r.status === 401 ? { ids: [], products: [] } : {}),
      });
      return;
    }
    const body = (await r.json()) as { products?: FavoriteProduct[] };
    if (gen !== generation) return;
    const products = Array.isArray(body?.products) ? body.products : [];
    setSnapshot({
      loading: false,
      error: null,
      products,
      ids: products.map((p) => p.product_id),
    });
  } catch {
    if (gen !== generation) return;
    setSnapshot({ loading: false, error: "network", ids: [], products: [] });
  }
}

/**
 * Add a favourite. Idempotent on the server, so a retry after a
 * timeout is safe. Resolves to true when the request succeeded.
 */
export async function addFavorite(productId: number): Promise<boolean> {
  return writeFavorite(productId, "PUT");
}

/**
 * Remove a favourite. Removing one that is not there is not an error
 * — the account simply does not have it.
 */
export async function removeFavorite(productId: number): Promise<boolean> {
  return writeFavorite(productId, "DELETE");
}

async function writeFavorite(
  productId: number,
  method: "PUT" | "DELETE",
): Promise<boolean> {
  setSnapshot({ error: null });
  try {
    const r = await fetch(`/api/auth/favorites/${productId}`, {
      method,
      credentials: "same-origin",
      headers: authHeaders(),
    });
    if (!r.ok) {
      setSnapshot({ error: `HTTP ${r.status}` });
      return false;
    }
    // The authoritative list comes from the server (it applies the
    // visibility rules), so re-read it instead of guessing locally.
    await loadFavorites();
    return true;
  } catch {
    setSnapshot({ error: "network" });
    return false;
  }
}

/**
 * Toggle a favourite. Resolves to the resulting state (true = now a
 * favourite), or null when the write failed — the caller shows an
 * error instead of silently pretending the write happened.
 *
 * A rapid double-click must produce ONE write, not two: the second
 * click would otherwise issue the OPPOSITE write (an un-favourite) a
 * few milliseconds later. The product is therefore locked while the
 * write is in flight AND for a short cooldown after it succeeds. A
 * FAILED write releases the lock at once — the user must be able to
 * retry immediately.
 */
const inFlight = new Map<number, Promise<boolean | null>>();
const lockTimers = new Map<number, ReturnType<typeof setTimeout>>();

/** How long a successful write keeps the product locked (ms). */
const WRITE_COOLDOWN_MS = 500;

export function toggleFavorite(
  productId: number,
): Promise<boolean | null> {
  const existing = inFlight.get(productId);
  if (existing) return existing;

  const wasFavorite = snapshot.ids.includes(productId);
  setSnapshot({ pending: [...snapshot.pending, productId] });
  const run = (wasFavorite
    ? removeFavorite(productId)
    : addFavorite(productId)
  )
    .then((ok) => {
      if (!ok) return null;
      // Keep the lock for a short while so a double-click cannot slip a
      // second, opposite write through. The window matches the
      // platform's own double-click threshold. The button is DISABLED
      // for the whole of it, so a click that is going to be absorbed is
      // visibly refused rather than silently dropped.
      setSnapshot({ locked: [...snapshot.locked, productId] });
      const timer = setTimeout(() => {
        inFlight.delete(productId);
        lockTimers.delete(productId);
        setSnapshot({
          locked: snapshot.locked.filter((id) => id !== productId),
        });
      }, WRITE_COOLDOWN_MS);
      lockTimers.set(productId, timer);
      return !wasFavorite;
    })
    .catch(() => null)
    .finally(() => {
      // The write itself is done: the button stops spinning. The lock
      // (if any) lives on separately.
      setSnapshot({
        pending: snapshot.pending.filter((id) => id !== productId),
      });
      if (!lockTimers.has(productId)) {
        // The write failed — release the lock so a retry can go through.
        inFlight.delete(productId);
      }
    });
  inFlight.set(productId, run);
  return run;
}

/** True while a write for this product is in flight. */
export function isFavoritePending(productId: number): boolean {
  return snapshot.pending.includes(productId);
}

/**
 * True while the product is inside the post-write cooldown — the
 * window in which a click would be absorbed to protect against a
 * double-click. The button is disabled for it, so the refusal is
 * visible.
 */
export function isFavoriteLocked(productId: number): boolean {
  return snapshot.locked.includes(productId);
}

/** Subscribe to the shared favourites snapshot (React hook). */
export { subscribe, getSnapshot };
